import React, { useState, useEffect } from 'react';
import { Star, ChevronLeft, ChevronRight, Quote, Loader2 } from 'lucide-react';
import axios from 'axios';
import { useLanguage } from '../../context/LanguageContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TestimonialsSection = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const { t } = useLanguage();

  useEffect(() => {
    const fetchTestimonials = async () => {
      try {
        const response = await axios.get(`${API}/testimonials`);
        setTestimonials(response.data);
      } catch (error) {
        console.error('Error fetching testimonials:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchTestimonials();
  }, []);

  const nextTestimonial = () => {
    setCurrentIndex((prev) =>
      prev === testimonials.length - 1 ? 0 : prev + 1
    );
  };

  const prevTestimonial = () => {
    setCurrentIndex((prev) =>
      prev === 0 ? testimonials.length - 1 : prev - 1
    );
  };

  if (loading) {
    return (
      <section id="testimonials" className="section-padding bg-transparent">
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 size={40} className="animate-spin text-[#C4B5A0]" />
        </div>
      </section>
    );
  }

  if (testimonials.length === 0) {
    return null;
  }

  const currentTestimonial = testimonials[currentIndex];

  return (
    <section id="testimonials" className="section-padding bg-transparent">
      <div className="max-w-5xl mx-auto">
        {/* Section Header */}
        <div className="text-center max-w-2xl mx-auto mb-16">
          <p className="text-caption text-[#C4B5A0] mb-4">
            {t('testimonials.label')}
          </p>
          <h2 className="heading-secondary text-4xl md:text-5xl text-[#2C2C2C] mb-4">
            {t('testimonials.title')}
          </h2>
          <div className="divider" />
          <p className="text-body text-[#6B4E37]">
            {t('testimonials.subtitle')}
          </p>
        </div>

        {/* Testimonial Card */}
        <div className="relative">
          <div className="bg-[#FAF9F7] rounded-2xl shadow-xl p-8 md:p-12 lg:p-16">
            {/* Quote Icon */}
            <div className="absolute top-8 left-8 md:top-12 md:left-12">
              <Quote size={48} className="text-[#D4C4B0]/40" />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-center">
              {/* Image */}
              <div className="md:col-span-1">
                <div className="aspect-square max-w-[200px] mx-auto rounded-full overflow-hidden shadow-lg border-4 border-[#D4C4B0]/30">
                  <img
                    src={currentTestimonial.image}
                    alt={currentTestimonial.couple}
                    className="img-cover"
                  />
                </div>
              </div>

              {/* Content */}
              <div className="md:col-span-2 text-center md:text-left">
                {/* Stars */}
                <div className="flex items-center justify-center md:justify-start gap-1 mb-6">
                  {[...Array(currentTestimonial.rating)].map((_, i) => (
                    <Star
                      key={i}
                      size={18}
                      className="text-[#C4B5A0] fill-[#C4B5A0]"
                    />
                  ))}
                </div>

                {/* Text */}
                <blockquote className="text-body text-lg md:text-xl text-[#6B4E37] italic mb-8 leading-relaxed">
                  "{currentTestimonial.text}"
                </blockquote>

                {/* Couple Name */}
                <p className="font-['Cormorant_Garamond'] text-2xl text-[#2C2C2C]">
                  {currentTestimonial.couple}
                </p>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <div className="flex items-center justify-center gap-4 mt-8">
            <button
              onClick={prevTestimonial}
              className="w-12 h-12 rounded-full border border-[#C4B5A0] flex items-center justify-center text-[#6B4E37] hover:bg-[#C4B5A0] hover:text-white transition-all duration-300"
              aria-label="Testemunho anterior"
            >
              <ChevronLeft size={20} />
            </button>

            {/* Dots */}
            <div className="flex items-center gap-2">
              {testimonials.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentIndex(index)}
                  className={`w-2 h-2 rounded-full transition-all duration-300 ${
                    index === currentIndex
                      ? 'w-6 bg-[#C4B5A0]'
                      : 'bg-[#D4C4B0]/50 hover:bg-[#D4C4B0]'
                  }`}
                  aria-label={`Ir para testemunho ${index + 1}`}
                />
              ))}
            </div>

            <button
              onClick={nextTestimonial}
              className="w-12 h-12 rounded-full border border-[#C4B5A0] flex items-center justify-center text-[#6B4E37] hover:bg-[#C4B5A0] hover:text-white transition-all duration-300"
              aria-label="Próximo testemunho"
            >
              <ChevronRight size={20} />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;
